/**
 * Hand-coded social conversations and small-talk phrases
 * (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0.
 *
 * Highest-leverage J-L1 category. Includes ladder marquee
 * `decline-invitation` at A2/B1/B2 + heavy coverage of the J-L1 functional
 * gaps documented in DevDocs/brainstorms/situational-language-learning-research.md
 * (refusal, disagreement, opinion ownership, repair, hedged requests,
 * conversation closure).
 */

import type { SituationCard } from '../../situations/types.js';

const socialSmalltalk: SituationCard[] = [
  {
    id: 'social.weather-opener',
    situationId: 'social.weather-opener',
    front: 'Beautiful weather we\'re having, isn\'t it?',
    back: 'いい天気ですね。',
    category: 'social-smalltalk',
    cefr: 'A1',
    translations: { ja: 'いいてんきですね。' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person',
    pragmaticNote: {
      en: 'Weather is THE canonical small-talk opener in English. Skipping it can feel abrupt to native speakers.',
      ja: '英語圏では天気の話が定番の挨拶代わり。いきなり用件に入ると唐突に感じられます。'
    }
  },
  {
    id: 'social.weekend-question',
    situationId: 'social.weekend-question',
    front: 'How was your weekend?',
    back: '週末はどうでしたか？',
    category: 'social-smalltalk',
    cefr: 'A2',
    translations: { ja: 'しゅうまつはどうでしたか？' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person',
    pragmaticNote: {
      en: 'Asking about weekends/vacations is standard rapport-building. Topics Japanese learners may consider personal (hobbies, family) are normal openers in English.',
      ja: '週末や休みの話を聞くのは普通のラポール構築。日本では立ち入った話に感じる趣味や家族の話題も英語では一般的です。'
    }
  },
  // Ladder marquee — declining an invitation across registers
  {
    id: 'social.decline-invitation.a2',
    situationId: 'social.decline-invitation.a2',
    front: "Sorry, I can't make it. Maybe next time.",
    back: 'すみません、行けません。また今度。',
    category: 'social-smalltalk',
    cefr: 'A2',
    translations: { ja: 'すみません、いけません。またこんど。' },
    speechAct: 'refusal',
    situationFamily: 'decline-invitation',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'social.decline-invitation.b1',
    situationId: 'social.decline-invitation.b1',
    front: "I'd love to, but I'm afraid I'm tied up that evening. Thanks for thinking of me, though.",
    back: 'ぜひ行きたいのですが、その晩は予定が詰まっていて……お誘いいただいてありがとうございます。',
    category: 'social-smalltalk',
    cefr: 'B1',
    translations: { ja: 'ぜひいきたいのですが、そのばんはよていがつまっていて……おさそいいただいてありがとうございます。' },
    speechAct: 'refusal',
    situationFamily: 'decline-invitation',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'B1 refusal pattern: positive opener ("I\'d love to") + reason + closing thanks. Skipping the opener reads as cold; skipping the reason reads as evasive.',
      ja: '英語の断り方は「行きたい気持ち＋理由＋お礼」の3点セットが定番。気持ちを伝える部分や理由を省くと冷たく感じられます。'
    }
  },
  {
    id: 'social.decline-invitation.b2',
    situationId: 'social.decline-invitation.b2',
    front: "That sounds wonderful, but I'll have to take a rain check this time — I've got a deadline I really can't push. Could we do it next week instead?",
    back: '素敵ですね。でも今回は遠慮させてください。動かせない締め切りがあって……来週ならどうですか？',
    category: 'social-smalltalk',
    cefr: 'B2',
    translations: { ja: 'すてきですね。でもこんかいはえんりょさせてください。うごかせないしめきりがあって……らいしゅうならどうですか？' },
    speechAct: 'refusal',
    situationFamily: 'decline-invitation',
    formality: 'polite',
    channel: 'in-person',
    notes: {
      en: '"Take a rain check" is a fixed idiom meaning "decline now but accept later." Pairs with a counter-offer ("Could we do it next week?") to soften the refusal.',
      ja: '「take a rain check」は「今回は断るけど次回は」の決まり文句。代替案を出すと角が立ちません。'
    }
  },
  {
    id: 'social.polite-disagreement',
    situationId: 'social.polite-disagreement',
    front: 'I see your point, but I think there\'s another way to look at it.',
    back: 'おっしゃることはわかります。ただ、別の見方もあるかなと思っていて。',
    category: 'social-smalltalk',
    cefr: 'B1',
    translations: { ja: 'おっしゃることはわかります。ただ、べつのみかたもあるかなとおもっていて。' },
    speechAct: 'disagreement',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: '"I see your point, but…" acknowledges before challenging. J-L1 learners often default to silence or agreement; this template makes disagreement safe.',
      ja: '英語ではまず相手の意見を認めてから反論するのが礼儀。沈黙や同調だけだと議論が深まりません。'
    },
    tags: ['pragmatics']
  },
  {
    id: 'social.repair-clarify',
    situationId: 'social.repair-clarify',
    front: "Sorry, could you say that again? I didn't quite catch it.",
    back: 'すみません、もう一度言っていただけますか？聞き取れなくて。',
    category: 'social-smalltalk',
    cefr: 'A2',
    translations: { ja: 'すみません、もういちどいっていただけますか？ききとれなくて。' },
    speechAct: 'repair',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: '"I didn\'t quite catch it" is gentler than "I don\'t understand" — softens the repair so the speaker doesn\'t feel they failed.',
      ja: '"I didn\'t quite catch it"は「わかりません」よりやわらかい言い方。話し手の責任にしないニュアンス。'
    }
  },
  {
    id: 'social.opinion-ownership',
    situationId: 'social.opinion-ownership',
    front: 'Personally, I think the second option makes more sense.',
    back: '個人的には、二番目の方が筋が通っていると思います。',
    category: 'social-smalltalk',
    cefr: 'B1',
    translations: { ja: 'こじんてきには、にばんめのほうがすじがとおっているとおもいます。' },
    speechAct: 'opinion',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'English expects first-person opinion ownership ("I think…", "Personally…"). Impersonal constructions like "It is said that…" feel evasive in conversation.',
      ja: '英語では「I think…」のように一人称で意見を述べるのが普通。「It is said…」のような匿名的な言い方は会話では避けられがち。'
    }
  },
  {
    id: 'social.compliment-receive',
    situationId: 'social.compliment-receive',
    front: 'Thank you, that\'s really kind of you to say.',
    back: 'ありがとうございます。そう言っていただけて嬉しいです。',
    category: 'social-smalltalk',
    cefr: 'A2',
    translations: { ja: 'ありがとうございます。そういっていただけてうれしいです。' },
    speechAct: 'gratitude',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'In English, accept compliments rather than deflecting them. Saying "no, no" to a compliment can read as discrediting the giver.',
      ja: '英語では褒め言葉は受け入れるのが基本。日本式に「いえいえ」と否定すると、褒めた相手の判断を否定したように受け取られることがあります。'
    }
  },
  {
    id: 'social.conversation-close',
    situationId: 'social.conversation-close',
    front: "Anyway, I should let you go — it was great talking to you.",
    back: 'それじゃ、そろそろ失礼しますね。お話しできてよかったです。',
    category: 'social-smalltalk',
    cefr: 'B1',
    translations: { ja: 'それじゃ、そろそろしつれいしますね。おはなしできてよかったです。' },
    speechAct: 'closing',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: '"I should let you go" is the polite English signal for ending a chat. It frames the close as for-the-other-person\'s-benefit.',
      ja: '"I should let you go"は会話を終える定型句。「相手のために」という形で切り上げるのが英語的。'
    }
  },
  {
    id: 'social.intro-self',
    situationId: 'social.intro-self',
    front: "Hi, I don't think we've met — I'm Alex.",
    back: 'はじめまして、アレックスです。',
    category: 'social-smalltalk',
    cefr: 'A1',
    translations: { ja: 'はじめまして、アレックスです。' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'social.what-do-you-do',
    situationId: 'social.what-do-you-do',
    front: 'So, what do you do?',
    back: 'お仕事は何をされていますか？',
    category: 'social-smalltalk',
    cefr: 'A2',
    translations: { ja: 'おしごとはなにをされていますか？' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person',
    pragmaticNote: {
      en: '"What do you do?" almost always means "What\'s your job?" in casual English — not "what are your hobbies?". Direct work questions are normal small talk.',
      ja: '「What do you do?」は普通「お仕事は？」の意味。日本ほど初対面で仕事を聞かないわけではなく、英語では普通の話題。'
    }
  },
  {
    id: 'social.diplomatic-disagreement',
    situationId: 'social.diplomatic-disagreement',
    front: "I take your point, though I'm not sure I see it quite the same way.",
    back: 'おっしゃることはわかるのですが、私は少し違う見方をしているかもしれません。',
    category: 'social-smalltalk',
    cefr: 'B2',
    translations: { ja: 'おっしゃることはわかるのですが、わたしはすこしちがうみかたをしているかもしれません。' },
    speechAct: 'disagreement',
    formality: 'formal',
    channel: 'in-person',
    notes: {
      en: 'B2 diplomatic disagreement: acknowledge → soften ("I\'m not sure") → present own view. The CEFR Companion 2018 puts this exponent firmly at B2.',
      ja: 'B2レベルの礼儀正しい異議。「相手を認める→自分の意見を弱めて述べる」の二段構えで角を立てません。'
    }
  },
  {
    id: 'social.expressing-uncertainty',
    situationId: 'social.expressing-uncertainty',
    front: "I'm not entirely sure, but my best guess would be around fifty.",
    back: 'はっきりとはわかりませんが、だいたい五十くらいかなと思います。',
    category: 'social-smalltalk',
    cefr: 'B2',
    translations: { ja: 'はっきりとはわかりませんが、だいたいごじゅうくらいかなとおもいます。' },
    speechAct: 'hedge',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'social.formal-greeting',
    situationId: 'social.formal-greeting',
    front: "It's a real pleasure to finally meet you in person.",
    back: 'やっと直接お会いできて光栄です。',
    category: 'social-smalltalk',
    cefr: 'C1',
    translations: { ja: 'やっとちょくせつおあいできてこうえいです。' },
    speechAct: 'opening',
    formality: 'formal',
    channel: 'in-person'
  }
];

export default socialSmalltalk;
